import {html, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {state} from "lit/decorators/state.js";
import {when} from "lit/directives/when.js";
import {DateTime, Interval} from "luxon";
import {AbstractLitInputComponent} from "../../abstract-lit-input.component";
import {get, translate} from "lit-translate";
import {isDateRangeOrderValid} from "../../../validators/date-range-order.validator";

@customElement(`ha-input-date-range`)
export class HaInputDateRangeComponent extends AbstractLitInputComponent<Interval> {

    @state()
    private _end!: DateTime

    @state()
    private _isMultiDay = false;

    @state()
    private _start!: DateTime;

    private get _isRangeInvalid(): boolean {
        if (!this._isMultiDay || !this._start || !this._end) {
            return false;
        }

        return !isDateRangeOrderValid(Interval.fromDateTimes(this._start, this._end.plus({day: 1})));
    }

    protected render(): unknown {

        return html`
            <div class="wrapper">
                <ha-input-date class="max9999-flexrow max1200-flexcolumn max992-flexrow  max355-flexcolumn date-range-start-item"
                            data-type="start"
                            label=${translate(this._isMultiDay ? 'components.ha-date-range.start' : 'components.ha-date-range.single-day')}
                            .value=${this._start}
                            @value-changed=${this._onStartDateChanged}
                            ?readonly=${this.readonly}
                ></ha-input-date>
                ${when(this._isMultiDay, () => html`
                    <ha-input-date class="max9999-flexrow max1200-flexcolumn max992-flexrow  max355-flexcolumn " data-type="end"
                                label=${translate('components.ha-date-range.end')}
                                .value=${this._end}
                                @value-changed=${this._onEndDateChanged}
                                ?readonly=${this.readonly}
                    ></ha-input-date>
                `)}
            </div>
            <ha-input-checkbox class="is-multi-day" label="Mehrere Tage?"
                               .value=${this._isMultiDay}
                               ?readonly=${this.readonly}
                               is-toggle
                               @value-changed=${this._onMultiDayChanged}
            ></ha-input-checkbox>
            ${this._renderHelperText()}
            ${when(this._isRangeInvalid, () => html`
                <ul class="input-error">
                    <li>${translate('components.ha-date-range.start-after-end-error')}</li>
                </ul>
            `)}
            ${this._renderErrors()}
        `
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);

        if (_changedProperties.has(`value`)) {
            // A backwards (start-after-end) Interval reports null start/end, which would crash the
            // sync below. Such a value can only be an echo of our own last dispatch (see the branch
            // below), and our local _start/_end already reflect it, so simply ignoring it here is safe.
            if (!!this.value && this.value.isValid) {
                this._isMultiDay = (this.value!.count(`days`) ?? 0) > 1;
                this._start = this.value.start!;
                this._end = this.value.end!.minus({day: 1})!;
            }
        } else if (
            (_changedProperties.has(`_start`) && !!_changedProperties.get(`_start`)) ||
            (_changedProperties.has(`_end`) && !!_changedProperties.get(`_end`))
        ) {
            this.dispatchEvent(
                new CustomEvent(`value-changed`, {detail: {value: Interval.fromDateTimes(this._start, this._isMultiDay ? this._end.plus({day: 1}) : this._start.plus({day: 1}))}})
            );
        }

        this._updateInvalidRangeState();
    }

    private _updateInvalidRangeState(): void {
        this.classList.toggle(`range-invalid`, this._isRangeInvalid);

        if (this._isRangeInvalid) {
            this.setAttribute(`title`, get(`components.ha-date-range.start-after-end-error`));
        } else {
            this.removeAttribute(`title`);
        }
    }

    private _onEndDateChanged(evt: CustomEvent<{ value: DateTime }>): void {
        if (this.readonly) {
            return;
        }

        let newEndDate = evt.detail.value;
        if (!newEndDate) {
            newEndDate = this._end.startOf(`day`);
        }

        this._end = newEndDate;
    }

    private _onMultiDayChanged(evt: CustomEvent<{ value: any }>): void {
        if (this.readonly) {
            return;
        }

        if (evt.detail.value === this._isMultiDay) {
            return;
        }

        this._isMultiDay = evt.detail.value;
        this._end = this._isMultiDay ? this._start.plus({day: 1}) : this._start;
    }

    private _onStartDateChanged(evt: CustomEvent<{ value: DateTime }>): void {
        if (this.readonly) {
            return;
        }

        let newStartDate = evt.detail.value;
        if (!newStartDate) {
            newStartDate = this._start.startOf(`day`);
        }

        if (!this._isMultiDay) {
            this._end = newStartDate.plus({day: 1});
        }

        this._start = newStartDate;
    }
}